import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Slider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CameraAlt as CameraIcon,
  PhotoLibrary as GalleryIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { useCloseOnBack } from '../hooks/useCloseOnBack';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../utils/cropImage';

/**
 * Componente de upload de foto de perfil com crop circular
 * Suporta: drag-and-drop, seleção de arquivo, câmera e galeria (mobile)
 */
export default function ProfilePhotoUpload({ 
  open, 
  onClose, 
  onUpload,
  currentPhotoUrl,
  title = 'Foto de Perfil',
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Handler de crop completo
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Validar arquivo
  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Formato não suportado. Use JPG, PNG ou WebP.');
    }

    if (file.size > maxSize) {
      throw new Error('Arquivo muito grande. Máximo 5MB.');
    }
  };

  // Processar arquivo selecionado
  const processFile = (file) => {
    try {
      validateFile(file);
      setError(null);

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
      });
      reader.readAsDataURL(file);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handler de seleção de arquivo
  const onFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      processFile(file);
    }
  };

  // Handler de drag and drop
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      processFile(file);
    }
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  // Handler de upload final
  const handleUpload = async () => {
    try {
      setUploading(true);
      setError(null);

      // Gerar imagem cropada
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Converter blob para file
      const file = new File([croppedImage], 'profile-photo.jpg', { type: 'image/jpeg' });

      // Chamar callback de upload
      await onUpload(file);

      // Fechar dialog em caso de sucesso
      handleClose();
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      setError(err.message || 'Erro ao processar imagem');
    } finally {
      setUploading(false);
    }
  };

  // Resetar estado ao fechar
  const handleClose = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
    setUploading(false);
    onClose();
  };

  // Voltar do celular fecha a janela (tela cheia) em vez de sair da tela — utils/backButtonLayers.js.
  useCloseOnBack(open && isMobile, handleClose);

  // Abrir seleção de arquivo
  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  // Abrir câmera (mobile)
  const handleOpenCamera = () => {
    cameraInputRef.current?.click();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!imageSrc ? (
          // Área de seleção de arquivo
          <Box>
            {/* Input escondido para arquivo */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={onFileChange}
              style={{ display: 'none' }}
            />

            {/* Input escondido para câmera (mobile) */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="user"
              onChange={onFileChange}
              style={{ display: 'none' }}
            />

            {/* Drag and Drop Area */}
            <Box
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              sx={{
                border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
                transition: 'all 0.2s',
                cursor: 'pointer',
              }}
              onClick={handleSelectFile}
            >
              <UploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isMobile ? 'Toque para selecionar' : 'Arraste uma foto ou clique para selecionar'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                JPG, PNG ou WebP - Máximo 5MB
              </Typography>
            </Box>

            {/* Botões mobile */}
            {isMobile && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<CameraIcon />}
                  onClick={handleOpenCamera}
                >
                  Câmera
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GalleryIcon />}
                  onClick={handleSelectFile}
                >
                  Galeria
                </Button>
              </Box>
            )}

            {/* Preview da foto atual */}
            {currentPhotoUrl && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Foto atual
                </Typography>
                <Box
                  component="img"
                  src={currentPhotoUrl}
                  alt="Foto atual"
                  // photoUrl agora é sempre emitido por convenção pela API (photo_id dropado);
                  // se o arquivo não existe no CDN, esconde o preview inteiro (404 esperado).
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `2px solid ${theme.palette.divider}`,
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          // Área de crop
          <Box>
            {/* Cropper circular */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: isMobile ? 300 : 400,
                backgroundColor: 'black',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>

            {/* Controles de zoom */}
            <Box sx={{ mt: 3, px: 2 }}>
              <Typography variant="body2" gutterBottom display="flex" alignItems="center" gap={1}>
                <ZoomOutIcon fontSize="small" />
                Zoom
                <ZoomInIcon fontSize="small" />
              </Typography>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, value) => setZoom(value)}
                sx={{ width: '100%' }}
              />
            </Box>

            {/* Botão para trocar foto */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                size="small"
                onClick={() => {
                  setImageSrc(null);
                  setCrop({ x: 0, y: 0 });
                  setZoom(1);
                }}
              >
                Escolher outra foto
              </Button>
            </Box>

            {/* Dica */}
            <Alert severity="info" sx={{ mt: 2 }}>
              Posicione e ajuste o zoom para enquadrar sua foto no círculo
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!imageSrc || uploading}
          startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
        >
          {uploading ? 'Enviando...' : 'Salvar Foto'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
